import React, { useEffect, useState, cloneElement, useMemo, useRef } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Image } from "@/components/common/image";
import { Video } from "./video";
import AgentVideoController from "../agent-modal-video/agent-video-controller";

export interface ImagePreviewModalProps {
  open?: boolean;
  trigger?: React.ReactElement;
  imageUrls?: string[];
  videoUrl?: string;
  posterUrl?: string;
  currentIndex?: number;
  onOpenChange?: (open: boolean) => void;
}

export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  open,
  onOpenChange,
  trigger,
  imageUrls = [],
  videoUrl,
  posterUrl,
  currentIndex = 0,
}) => {
  const SAMPLE_VIDEO_URL = "https://sg-miuta-resource.oss-accelerate.aliyuncs.com/push_videos/n8n_testforadmin_1770257395_052e15ac-54c1-40e9-b358-e9f65e21e9b4_1/20260205_101744.mp4";

  const [contentHeight, setContentHeight] = useState(720);
  const [contentWidth, setContentWidth] = useState(Math.round(720 * 9 / 16));
  const videoRef = useRef<HTMLVideoElement>(null);
  const [index, setIndex] = useState(currentIndex ?? 0);

  const [internalOpen, setInternalOpen] = useState(false);
  const isTriggerMode = !!trigger;
  const currentOpen = isTriggerMode ? internalOpen : Boolean(open);

  const handleOpenChange = (value: boolean) => {
    if (isTriggerMode) setInternalOpen(value);
    else onOpenChange?.(value);
  };

  // 尺寸计算逻辑
  useEffect(() => {
    if (!currentOpen) return;
    const updateSize = () => {
      const h = Math.round(window.innerHeight * 0.88);
      const height = Math.max(560, Math.min(1080, h));
      const width = Math.round((height * 9) / 16);
      setContentHeight(height);
      setContentWidth(width);
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [currentOpen]);

  // 同步外部索引
  useEffect(() => {
    if (currentIndex !== null) setIndex(currentIndex);
  }, [currentIndex]);

  const multiple = useMemo(() => imageUrls.length > 1, [imageUrls]);

  const handlePrev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIndex((prev) => (prev - 1 + imageUrls.length) % imageUrls.length);
  };

  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIndex((prev) => (prev + 1) % imageUrls.length);
  };

  // 键盘快捷键支持
  useEffect(() => {
    if (!currentOpen || !multiple) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowDown" || e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentOpen, multiple, imageUrls.length]);

  const content = (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
      <div className="controls-container fixed inset-0 z-[60] pointer-events-none">
        <div className="relative w-full h-full">
          {/* 关闭按钮 */}
          <button
            onClick={() => handleOpenChange(false)}
            className="absolute pointer-events-auto flex items-center justify-center w-10 h-10 rounded-[10px] bg-white hover:bg-white/60 text-white transition-all active:scale-95"
            style={{
              left: `calc(50% + ${contentWidth / 2}px + 20px)`,
              top: `calc(50% - ${contentHeight / 2}px)`,
            }}
          >
            <X className="w-6 h-6 text-[rgba(71,84,103,1)]" />
          </button>

          {/* 切换按钮组 */}
          {multiple && (
            <div
              className="absolute pointer-events-auto flex flex-col gap-4"
              style={{
                left: `calc(50% + ${contentWidth / 2}px + 20px)`,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <button
                onClick={handlePrev}
                className="w-10 h-10 rounded-[10px] bg-[rgba(255,255,255,0.2)] hover:bg-white/40 text-white flex items-center justify-center transition-all active:scale-90"
              >
                <ChevronUp className="w-6 h-6 text-white" />
              </button>
              <button
                onClick={handleNext}
                className="w-10 h-10 rounded-[10px] bg-[rgba(255,255,255,0.2)] hover:bg-white/40 text-white flex items-center justify-center transition-all active:scale-90"
              >
                <ChevronDown className="w-6 h-6 text-white" />
              </button>
            </div>
          )}
        </div>
      </div>
      <Dialog.Content
        // 点击控制层按钮时不触发关闭弹窗
        onPointerDownOutside={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest(".controls-container")) {
            e.preventDefault();
          }
        }}
        // 处理键盘以外的交互点击
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest(".controls-container")) {
            e.preventDefault();
          }
        }}
        className={cn(
          "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
          "rounded-2xl shadow-2xl overflow-hidden z-50 focus:outline-none",
          "bg-black"
        )}
        style={{ width: contentWidth, height: contentHeight }}
      >
        <div className="relative w-full h-full flex items-center justify-center">
          {videoUrl ? (
            <Video
              ref={videoRef}
              src={videoUrl}
              className="h-full w-auto"
              poster={posterUrl}
              autoPlay
              preload="auto"
              controls={false}
            />
          ) : <Image
            src={imageUrls[index] || ""}
            className="w-full h-full object-contain"
            alt={`Preview ${index}`}
          />}
        </div>
        {/* 播放控制条（底部覆盖） */}
        {videoUrl && <div className="absolute bottom-2 left-2 right-2">
          <AgentVideoController videoRef={videoRef} />
        </div>}
      </Dialog.Content>
    </Dialog.Portal>
  );

  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {isTriggerMode && trigger && (
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              trigger.props.onClick?.(e);
              handleOpenChange(true);
            },
          })}
        </Dialog.Trigger>
      )}
      {content}
    </Dialog.Root>
  );
};

export default ImagePreviewModal;